import API_CONFIG from '../config/api';

// Use centralized configuration
export const baseURL = API_CONFIG.BASE_URL;
const backendDomain = API_CONFIG.BASE_URL;


// IMPORTANT: `baseURL` is exposed on the SummaryApi default export as well.
// Several call sites (OrderCard ticket links, StockManagement stores fetch) read
// `SummaryApi.baseURL` directly — before this attribute existed those URLs resolved
// to `undefined/...` and quietly 404'd. Keep both shapes in sync.
const SummaryApi = {
    baseURL: backendDomain,
    signUp : {
url : `${backendDomain}/api/v1/signup`,
        method : "post"
    },
    signIn : {
url : `${backendDomain}/api/v1/signin`,
        method : "post"
    },
    googleSignIn : {
url : `${backendDomain}/api/v1/auth/google`,
        method : "post"
    },
    current_user : {
url : `${backendDomain}/api/v1/user-details`,
        method : "get"
    },
    logout_user : {
        // Phase: refresh-token rotation. Switched from the legacy GET /userLogout to
        // POST /auth/logout so the path-scoped refresh_token cookie (Path=/api/v1/auth)
        // reaches the handler and gets revoked. The old endpoint still works as a
        // clears-cookies-only fallback for any cached frontends in the wild.
        url : `${backendDomain}/api/v1/auth/logout`,
        method : 'post'
    },
    refresh_token : {
        url : `${backendDomain}/api/v1/auth/refresh`,
        method : 'post'
    },
    // V2 catalog admin endpoints (variants, SKUs, prices, price lists).
    // The frontend pages that use these call them with `credentials: 'include'`
    // — admin auth is enforced server-side via requireAdmin middleware.
    adminProductCatalog : {
        url : `${backendDomain}/api/v1/admin/products`,  // append `/:id/catalog`
        method : 'get'
    },
    // Variant matrix — Phase 2 of the variant + SKU UX rework. Both
    // sides addressed by appending `/:id/variant-matrix`.
    // GET returns the assembled grid (dimensions + rows + linked
    // SKUs + baseline prices). PUT accepts the whole grid back.
    adminVariantMatrix : {
        url : `${backendDomain}/api/v1/admin/products`,  // append `/:id/variant-matrix`
        method : 'get'
    },
    adminSaveVariantMatrix : {
        url : `${backendDomain}/api/v1/admin/products`,  // append `/:id/variant-matrix`
        method : 'put'
    },
    // Stock-back notification subscription (Phase 4 of the variant/
    // SKU UX rework). Public — anonymous shoppers OK.
    notifyStockBack : {
        url : `${backendDomain}/api/v1/notifications/stock-back`,
        method : 'post'
    },
    // Admin ops surface — list + delete subscriptions.
    adminStockBackList : {
        url : `${backendDomain}/api/v1/admin/notifications/stock-back`,
        method : 'get'
    },
    adminStockBackDelete : {
        url : `${backendDomain}/api/v1/admin/notifications/stock-back`,  // append /:id
        method : 'delete'
    },
    adminListSkus : {
        url : `${backendDomain}/api/v1/admin/skus`,
        method : 'get'
    },
    adminInventoryDashboard : {
        url : `${backendDomain}/api/v1/admin/inventory/dashboard`,
        method : 'get'
    },
    adminInventoryMovements : {
        url : `${backendDomain}/api/v1/admin/inventory/movements`,
        method : 'get'
    },
    adminPricingMatrix : {
        url : `${backendDomain}/api/v1/admin/pricing/matrix`,
        method : 'get'
    },
    adminUpsertBaselinePrice : {
        url : `${backendDomain}/api/v1/admin/pricing/upsert`,
        method : 'post'
    },
    adminListStocktakes : {
        url : `${backendDomain}/api/v1/admin/stocktakes`,
        method : 'get'
    },
    adminCreateStocktake : {
        url : `${backendDomain}/api/v1/admin/stocktakes`,
        method : 'post'
    },
    adminGetStocktake : {
        url : `${backendDomain}/api/v1/admin/stocktakes`,   // append `/:id`
        method : 'get'
    },
    adminUpdateStocktakeCounts : {
        url : `${backendDomain}/api/v1/admin/stocktakes`,   // append `/:id/count`
        method : 'PATCH'
    },
    adminCommitStocktake : {
        url : `${backendDomain}/api/v1/admin/stocktakes`,   // append `/:id/commit`
        method : 'post'
    },
    adminCancelStocktake : {
        url : `${backendDomain}/api/v1/admin/stocktakes`,   // append `/:id/cancel`
        method : 'post'
    },
    adminUpdateSku : {
        url : `${backendDomain}/api/v1/admin/skus`,  // append `/:id`
        method : 'PATCH'
    },
    adminListPriceLists : {
        url : `${backendDomain}/api/v1/admin/price-lists`,
        method : 'get'
    },
    adminCreatePriceList : {
        url : `${backendDomain}/api/v1/admin/price-lists`,
        method : 'post'
    },
    adminCreatePrice : {
        url : `${backendDomain}/api/v1/admin/prices`,
        method : 'post'
    },
    adminUpdatePrice : {
        url : `${backendDomain}/api/v1/admin/prices`,  // append `/:id`
        method : 'PATCH'
    },
    adminDeletePrice : {
        url : `${backendDomain}/api/v1/admin/prices`,  // append `/:id`
        method : 'delete'
    },
    adminCreateVariant : {
        url : `${backendDomain}/api/v1/admin/variants`,
        method : 'post'
    },
    adminUpdateVariant : {
        url : `${backendDomain}/api/v1/admin/variants`,  // append `/:id`
        method : 'PATCH'
    },
    adminDeleteVariant : {
        url : `${backendDomain}/api/v1/admin/variants`,  // append `/:id`
        method : 'delete'
    },
    adminUpdatePriceList : {
        url : `${backendDomain}/api/v1/admin/price-lists`,  // append `/:id`
        method : 'PATCH'
    },
    adminDeletePriceList : {
        url : `${backendDomain}/api/v1/admin/price-lists`,  // append `/:id`
        method : 'delete'
    },
    adminAdjustStock : {
        url : `${backendDomain}/api/v1/admin/products`,  // append `/:id/stock-adjust`
        method : 'post'
    },
    adminListPlugins : {
        url : `${backendDomain}/api/v1/admin/plugins`,
        method : 'get'
    },
    // Brand directory (Bundle A1)
    adminListBrands : {
        url : `${backendDomain}/api/v1/admin/brands`,
        method : 'get'
    },
    adminCreateBrand : {
        url : `${backendDomain}/api/v1/admin/brands`,
        method : 'post'
    },
    adminUpdateBrand : {
        url : `${backendDomain}/api/v1/admin/brands`,  // append `/:id`
        method : 'PATCH'
    },
    adminArchiveBrand : {
        url : `${backendDomain}/api/v1/admin/brands`,  // append `/:id`
        method : 'delete'
    },
    publicListBrands : {
        url : `${backendDomain}/api/v1/brands`,
        method : 'get'
    },
    // Category tree (Bundle A1)
    adminListCategories : {
        url : `${backendDomain}/api/v1/admin/categories`,
        method : 'get'
    },
    adminCategoryTree : {
        url : `${backendDomain}/api/v1/admin/categories/tree`,
        method : 'get'
    },
    publicCategoryTree : {
        url : `${backendDomain}/api/v1/categories/tree`,
        method : 'get'
    },
    // Same controller as the admin route, reachable by a vendor for their own
    // product form. See routes/vendor/portal.js.
    vendorCategoryTemplateByCategory : {
        url : `${backendDomain}/api/v1/vendor/category-templates/by-category`,
        method : 'get'
    },
    // Same controller as /admin/plugins, reachable by a vendor. Supplies the
    // event date / time / venue fields to the product form.
    vendorListPlugins : {
        url : `${backendDomain}/api/v1/vendor/plugins`,
        method : 'get'
    },
    adminCreateCategory : {
        url : `${backendDomain}/api/v1/admin/categories`,
        method : 'post'
    },
    adminUpdateCategory : {
        url : `${backendDomain}/api/v1/admin/categories`,  // append `/:id`
        method : 'PATCH'
    },
    adminArchiveCategory : {
        url : `${backendDomain}/api/v1/admin/categories`,  // append `/:id`
        method : 'delete'
    },
    publicListCategories : {
        url : `${backendDomain}/api/v1/categories`,
        method : 'get'
    },
    // Attribute definitions (Bundle A1 / Day 4)
    adminListAttributes : {
        url : `${backendDomain}/api/v1/admin/attribute-definitions`,
        method : 'get'
    },
    adminCreateAttribute : {
        url : `${backendDomain}/api/v1/admin/attribute-definitions`,
        method : 'post'
    },
    adminUpdateAttribute : {
        url : `${backendDomain}/api/v1/admin/attribute-definitions`,  // append `/:id`
        method : 'PATCH'
    },
    adminDeactivateAttribute : {
        url : `${backendDomain}/api/v1/admin/attribute-definitions`,  // append `/:id`
        method : 'delete'
    },
    // Category templates (Bundle A1 / Day 4)
    adminListCategoryTemplates : {
        url : `${backendDomain}/api/v1/admin/category-templates`,
        method : 'get'
    },
    adminCategoryTemplateByCategory : {
        url : `${backendDomain}/api/v1/admin/category-templates/by-category`,  // append `/:categoryId`
        method : 'get'
    },
    adminCreateCategoryTemplate : {
        url : `${backendDomain}/api/v1/admin/category-templates`,
        method : 'post'
    },
    adminUpdateCategoryTemplate : {
        url : `${backendDomain}/api/v1/admin/category-templates`,  // append `/:id`
        method : 'PATCH'
    },
    adminDeactivateCategoryTemplate : {
        url : `${backendDomain}/api/v1/admin/category-templates`,  // append `/:id`
        method : 'delete'
    },
    // Per-product attribute map (Day 5 of Bundle A1) — used by ProductForm
    // on edit-mode mount to hydrate the category-template attribute fieldset.
    adminProductAttributes : {
        url : `${backendDomain}/api/v1/admin/products`,   // append `/:id/attributes`
        method : 'get'
    },
    allUser : {
url : `${backendDomain}/api/v1/all-user`,
        method : 'get'
    },
    allOrders : {
url : `${backendDomain}/api/v1/all-orders`,
        method : 'get'
    },
    updateOrderStatus : {
url : `${backendDomain}/api/v1/update-order-status`,
        method : 'post'
    },
    orderStatistics : {
url : `${backendDomain}/api/v1/order-statistics`,
        method : 'get'
    },
    updateUser : {
url : `${backendDomain}/api/v1/update-user`,
        method : "post"
    },
    addUser : {
url : `${backendDomain}/api/v1/add-user`,
        method : "post"
    },
    deleteUser : {
url : `${backendDomain}/api/v1/delete-user`,
        method : "post"
    },
    uploadProduct : {
url : `${backendDomain}/api/v1/upload-product`,
        method : 'post'
    },
    allProduct : {
url : `${backendDomain}/api/v1/get-product`,
        method : 'get'
    },
    updateProduct : {
url : `${backendDomain}/api/v1/update-product`,
        method  : 'post'
    },
    deleteProduct : {
url : `${backendDomain}/api/v1/delete-product`,
        method  : 'post'
    },
    categoryProduct : {
url : `${backendDomain}/api/v1/get-categoryProduct`,
        method : 'get'
    },
    // Groceries vertical (Phase 3 of marketplace-V1). Public storefront endpoints.
    groceriesProducts : {
        url : `${backendDomain}/api/v1/groceries/products`,
        method : 'get'
    },
    groceriesCategories : {
        url : `${backendDomain}/api/v1/groceries/categories`,
        method : 'get'
    },
    // Fashion vertical (Phase 4 of marketplace-V1). Public storefront endpoints.
    fashionProducts : {
        url : `${backendDomain}/api/v1/fashion/products`,
        method : 'get'
    },
    fashionFacets : {
        url : `${backendDomain}/api/v1/fashion/facets`,
        method : 'get'
    },
    fashionCategories : {
        url : `${backendDomain}/api/v1/fashion/categories`,
        method : 'get'
    },
    // Electronics vertical (Phase 5 of marketplace-V1). Public storefront endpoints.
    electronicsProducts : {
        url : `${backendDomain}/api/v1/electronics/products`,
        method : 'get'
    },
    electronicsFacets : {
        url : `${backendDomain}/api/v1/electronics/facets`,
        method : 'get'
    },
    electronicsCategories : {
        url : `${backendDomain}/api/v1/electronics/categories`,
        method : 'get'
    },
    // Admin drilldown — Phase 6d follow-up. Warranty aging across all
    // electronics units sold (one row per unit; refunds excluded).
    electronicsWarrantyAging : {
        url : `${backendDomain}/api/v1/admin/electronics/warranty-aging`,
        method : 'get'
    },
    // Admin drilldown — Phase 6d follow-up. Grocery batch receiving:
    // GET lists RECEIPT movements with lot + expiry aging; POST records
    // a new batch through the inventory ledger.
    groceriesBatches : {
        url : `${backendDomain}/api/v1/admin/groceries/batches`,
        method : 'get'
    },
    groceriesRecordBatch : {
        url : `${backendDomain}/api/v1/admin/groceries/batches`,
        method : 'post'
    },
    // Admin drilldown — Phase 6d follow-up. Fashion size-run
    // replenishment: one row per active variant with on-hand qty at the
    // target store and out/low/ok bucketing.
    fashionReplenishment : {
        url : `${backendDomain}/api/v1/admin/fashion/replenishment`,
        method : 'get'
    },
    categoryWiseProduct : {
url : `${backendDomain}/api/v1/category-product`,
        method : 'post'
    },
    productDetails : {
url : `${backendDomain}/api/v1/product-details`,
        method : 'post'
    },
    preorders : {
        url : `${backendDomain}/api/v1/preorders`,
        method : 'get'
    },
    adminPreorders : {
        url : `${backendDomain}/api/v1/admin/preorders`,
        method : 'get'
    },
    adminPreorderAllocate : {
        url : `${backendDomain}/api/v1/admin/preorders/allocate`,
        method : 'post'
    },
    priceLookup : {
        url : `${backendDomain}/api/v1/prices/lookup`,
        method : 'get'
    },
    addToCartProduct : {
url : `${backendDomain}/api/v1/addtocart`,
        method : 'post' 
    },
    addToCartProductCount : {
url : `${backendDomain}/api/v1/countAddToCartProduct`,
        method : 'get'
    },
    addToCartProductView : {
url : `${backendDomain}/api/v1/view-card-product`,
        method : 'get'
    },
    updateCartProduct : {
url : `${backendDomain}/api/v1/update-cart-product`,
        method : 'post'
    },
    deleteCartProduct : {
url : `${backendDomain}/api/v1/delete-cart-product`,
        method : 'post'
    },
    initiatePayment : {
url : `${backendDomain}/api/v1/initiate-payment`,
        method : 'post'
    },
    createOrder : {
url : `${backendDomain}/api/v1/create-order`,
        method :'post'
    },
    getOrderDetails: {
url: `${backendDomain}/api/v1/orders`,
        method: 'GET'
    },
    getUserOrders : {
url : `${backendDomain}/api/v1/user/orders`,
        method : 'get'
    },
    lookupOrder : {
url : `${backendDomain}/api/v1/orders/lookup`,
        method : 'get'
    },
    getPublicOrder : {
url : `${backendDomain}/api/v1/orders/public`,
        method : 'get'
    },
    searchProduct : {
url : `${backendDomain}/api/v1/search`,
        method : 'get'
    },
    // Wishlist
    getWishlist : {
url : `${backendDomain}/api/v1/wishlist`,
        method : 'get'
    },
    putWishlist : {
url : `${backendDomain}/api/v1/wishlist`,
        method : 'put'
    },
    toggleWishlist : {
url : `${backendDomain}/api/v1/wishlist/toggle`,
        method : 'post'
    },
    // Behavior tracking & recommendations
    trackBehavior : {
url : `${backendDomain}/api/v1/behavior/track`,
        method : 'post'
    },
    getRecommendations : {
url : `${backendDomain}/api/v1/recommendations`,
        method : 'get'
    },
    // Banners (public)
    banners : {
url : `${backendDomain}/api/v1/banners`,
        method : 'get'
    },
    // Banners (admin)
    adminBannersList : {
url : `${backendDomain}/api/v1/admin/banners`,
        method : 'get'
    },
    adminBannerCreate : {
url : `${backendDomain}/api/v1/admin/banners`,
        method : 'post'
    },
    adminBannerUpdate : {
url : `${backendDomain}/api/v1/admin/banners`, 
        method : 'put'
    },
    adminBannerDelete : {
url : `${backendDomain}/api/v1/admin/banners`,
        method : 'delete'
    },
    // Attendees master (Phase 6e of marketplace-V1). Cross-event admin listing.
    adminListAttendees : {
url : `${backendDomain}/api/v1/admin/attendees`,
        method : 'get'
    },
    adminAttendeeEvents : {
url : `${backendDomain}/api/v1/admin/attendees/events`,
        method : 'get'
    },
    adminVoidAttendee : {
url : `${backendDomain}/api/v1/admin/attendees`,   // append `/:code/void`
        method : 'post'
    },
    adminTransferAttendee : {
url : `${backendDomain}/api/v1/admin/attendees`,   // append `/:code/transfer`
        method : 'post'
    },
    // Unified ticket resend (Phase 6i of marketplace-V1). One endpoint that
    // handles order/code × email/sms/both. Old per-flow endpoints stay
    // wired for legacy consumers.
    adminResendTicket : {
url : `${backendDomain}/api/v1/admin/attendees/resend`,
        method : 'post'
    },
    adminBannersPurgeExpired : {
url : `${backendDomain}/api/v1/admin/banners/expired`,
        method : 'delete'
    },
    adminBannerUpload : {
url : `${backendDomain}/api/v1/admin/banners/upload`,
        method : 'post'
    },
    adminBannerReorder : {
url : `${backendDomain}/api/v1/admin/banners/reorder`,
        method : 'PATCH'
    },
    filterProduct : {
url : `${backendDomain}/api/v1/filter-product`,
        method : 'post'
    },
    payment : {
url : `${backendDomain}/api/v1/checkout`,
        method : 'post'
    },
    forgot_password : {
url : `${backendDomain}/api/v1/user/forgot-password`,
        method : 'put'
    },
    forgot_password_otp_verification : {
url : `${backendDomain}/api/v1/user/verify-forgot-password-otp`,
        method : 'put'
    },
    resetPassword : {
url : `${backendDomain}/api/v1/user/reset-password`,
        method : 'put'
    },
    refreshToken : {
url : `${backendDomain}/api/v1/user/refresh-token`,
        method : 'post'
    },
    // EcoCash Paynow payments
    initiateEcoCashPayment : {
url : `${backendDomain}/api/v1/payment/ecocash/initiate`,

        method : 'post'
    },
    checkEcoCashPaymentStatus : {
url : `${backendDomain}/api/v1/payment/ecocash/status`,
        method : 'post'
    },
    // Card/ZimSwitch hosted payments
    initiateCardPayment : {
url : `${backendDomain}/api/v1/payment/card/initiate`,
        method : 'post'
    },
    // Audit logs (SUPER_ADMIN only)
    getAuditLogs : {
url : `${backendDomain}/api/v1/audit-logs`,
        method : 'get'
    },
    getSmsLogs : {
url : `${backendDomain}/api/v1/admin/sms-logs`,
        method : 'get'
    },
    exportSmsLogs : {
url : `${backendDomain}/api/v1/admin/sms-logs/export`,
        method : 'get'
    },
    getUserActivity : {
url : `${backendDomain}/api/v1/user-activity`,
        method : 'get'
    },
    // Analytics
    analyticsSummary : {
url : `${backendDomain}/api/v1/admin/analytics/summary`,
        method : 'get'
    },
    analyticsOverview : {
        url : `${backendDomain}/api/v1/admin/analytics/overview`,
        method : 'get'
    },
    analyticsActivity : {
        url : `${backendDomain}/api/v1/admin/analytics/activity`,
        method : 'get'
    },
    analyticsSkuSales : {
        url : `${backendDomain}/api/v1/admin/analytics/sku-sales`,
        method : 'get'
    },
    analyticsProductSales : {
        url : `${backendDomain}/api/v1/admin/analytics/product-sales`,
        method : 'get'
    },
    // Store settings
    getActiveStoreCity : {
url : `${backendDomain}/api/v1/admin/store-settings/active-city`,
        method : 'get'
    },
    setActiveStoreCity : {
url : `${backendDomain}/api/v1/admin/store-settings/active-city`,
        method : 'put'
    },
    listStores : {
        url : `${backendDomain}/api/v1/stores`,
        method : 'get'
    },
    listSuppliers : {
        url : `${backendDomain}/api/v1/admin/suppliers`,
        method : 'get'
    },
    adminCreateSupplier : {
        url : `${backendDomain}/api/v1/admin/suppliers`,
        method : 'post'
    },
    adminUpdateSupplier : {
        url : `${backendDomain}/api/v1/admin/suppliers`, // append /:id
        method : 'patch'
    },
    adminCreateStore : {
        url : `${backendDomain}/api/v1/stores`,
        method : 'post'
    },
    adminUpdateStore : {
        url : `${backendDomain}/api/v1/stores`, // append /:id
        method : 'patch'
    },
    // Stock Management APIs
    stockOverview : {
url : `${backendDomain}/api/v1/stock/overview`,
        method : 'get'
    },
    restockProduct : {
url : `${backendDomain}/api/v1/stock/restock`,
        method : 'post'
    },
    adjustStock : {
url : `${backendDomain}/api/v1/stock/adjust`,
        method : 'post'
    },
    updateEOQ : {
url : `${backendDomain}/api/v1/stock/eoq/:productId`,
        method : 'put'
    },
    stockAudit : {
url : `${backendDomain}/api/v1/stock/audit`,
        method : 'get'
    },
    // Bulk Stock Management APIs
    clearDemoStock : {
url : `${backendDomain}/api/v1/stock/clear-demo`,
        method : 'post'
    },
    bulkUploadStock : {
url : `${backendDomain}/api/v1/stock/bulk-upload`,
        method : 'post'
    },
    downloadStockTemplate : {
url : `${backendDomain}/api/v1/stock/download-template`,
        method : 'get'
    },
    stockSummary : {
url : `${backendDomain}/api/v1/stock/summary`,
        method : 'get'
    },
    // Delivery APIs
    calculateDeliveryRoute : {
url : `${backendDomain}/api/v1/delivery/calculate-route`,
        method : 'post'
    },
    calculateDeliveryFee : {
url : `${backendDomain}/api/v1/delivery/calculate-fee`,
        method : 'post'
    },
    getStoreLocation : {
url : `${backendDomain}/api/v1/delivery/store-location`,
        method : 'get'
    },
    validateDeliveryAddress : {
url : `${backendDomain}/api/v1/delivery/validate-address`,
        method : 'post'
    },
    getDeliveryZones : {
url : `${backendDomain}/api/v1/delivery/zones`,
        method : 'get'
    },
    // Delivery-only booking
    deliveryBook : {
url : `${backendDomain}/api/v1/delivery/book`,
        method : 'post'
    },
    deliveryJobsList : {
url : `${backendDomain}/api/v1/delivery/jobs`,
        method : 'get'
    },
    deliveryJobPublic : {
url : `${backendDomain}/api/v1/delivery/jobs/:jobNumber`,
        method : 'get'
    },
    deliveryJobUpdateStatus : {
url : `${backendDomain}/api/v1/delivery/jobs/:jobId/status`,
        method : 'put'
    },
    // Tickets
    validateTicket : {
url : `${backendDomain}/api/v1/tickets/validate`,
        method : 'post'
    },
    getTicketByCode : {
url : `${backendDomain}/api/v1/tickets`, // append /:code at callsite
        method : 'get'
    },
    // Unified ticket resend — Phase 6i (Ticketing Tier 2.1) + follow-up.
    // Body: { scope: 'order'|'code', target: string, channel: 'email'|'sms'|'both' }
    // Two surfaces share one controller:
    //   - `resendTickets`      → /api/v1/tickets/resend        (owner-or-admin)
    //   - `adminResendAttendee`→ /api/v1/admin/attendees/resend (admin only)
    // Use `resendTickets` from customer surfaces; `adminResendAttendee`
    // only for admin flows that need the requireAdmin gate.
    resendTickets : {
url : `${backendDomain}/api/v1/tickets/resend`,
        method : 'post'
    },
    adminResendAttendee : {
url : `${backendDomain}/api/v1/admin/attendees/resend`,
        method : 'post'
    },
    eventAttendees : {
url : `${backendDomain}/api/v1/events`, // append /:eventId/attendees
        method : 'get'
    },
    eventAttendeesCsv : {
url : `${backendDomain}/api/v1/events`, // append /:eventId/attendees.csv
        method : 'get'
    },

    // ── Vendor marketplace (Stage 4) ────────────────────────────────────
    // Onboarding — any authenticated user.
    vendorApply : {
        url : `${backendDomain}/api/v1/vendor/apply`,
        method : 'post'
    },
    vendorApplicationStatus : {
        url : `${backendDomain}/api/v1/vendor/application-status`,
        method : 'get'
    },
    // Self-service portal — VENDOR role.
    vendorProfile : {
        url : `${backendDomain}/api/v1/vendor/profile`,
        method : 'get'
    },
    vendorUpdateProfile : {
        url : `${backendDomain}/api/v1/vendor/profile`,
        method : 'PATCH'
    },
    vendorDashboard : {
        url : `${backendDomain}/api/v1/vendor/dashboard`,
        method : 'get'
    },
    vendorProducts : {
        url : `${backendDomain}/api/v1/vendor/products`,
        method : 'get'
    },
    vendorOrders : {
        url : `${backendDomain}/api/v1/vendor/orders`,
        method : 'get'
    },
    vendorPayouts : {
        url : `${backendDomain}/api/v1/vendor/payouts`,
        method : 'get'
    },
    // Ticket holders for the vendor's own events (scoped server-side).
    vendorAttendees : {
        url : `${backendDomain}/api/v1/vendor/attendees`,
        method : 'get'
    },
    vendorAttendeeEvents : {
        url : `${backendDomain}/api/v1/vendor/attendees/events`,
        method : 'get'
    },
    // Chart series for the seller dashboard (paid orders only, net of refunds).
    vendorAnalytics : {
        url : `${backendDomain}/api/v1/vendor/analytics`,
        method : 'get'
    },
    // Vendor product writes — same controllers as admin, vendor-scoped.
    vendorUploadProduct : {
        url : `${backendDomain}/api/v1/vendor/products`,
        method : 'post'
    },
    vendorUpdateProduct : {
        url : `${backendDomain}/api/v1/vendor/products/update`,
        method : 'post'
    },
    vendorVariantMatrix : {
        url : `${backendDomain}/api/v1/vendor/products`,  // append `/:id/variant-matrix`
        method : 'get'
    },
    vendorSaveVariantMatrix : {
        url : `${backendDomain}/api/v1/vendor/products`,  // append `/:id/variant-matrix`
        method : 'put'
    },
    vendorAdjustStock : {
        url : `${backendDomain}/api/v1/vendor/products`,  // append `/:id/stock-adjust`
        method : 'post'
    },
    // Admin vendor management.
    adminListVendors : {
        url : `${backendDomain}/api/v1/admin/vendors`,
        method : 'get'
    },
    adminGetVendor : {
        url : `${backendDomain}/api/v1/admin/vendors`,  // append `/:id`
        method : 'get'
    },
    adminCreateVendor : {
        url : `${backendDomain}/api/v1/admin/vendors`,
        method : 'post'
    },
    adminUpdateVendor : {
        url : `${backendDomain}/api/v1/admin/vendors`,  // append `/:id`
        method : 'PATCH'
    },
    adminUpdateVendorStatus : {
        url : `${backendDomain}/api/v1/admin/vendors`,  // append `/:id/status`
        method : 'PATCH'
    },
    adminVendorProducts : {
        url : `${backendDomain}/api/v1/admin/vendors`,  // append `/:id/products`
        method : 'get'
    },
    adminVendorPayoutSummary : {
        url : `${backendDomain}/api/v1/admin/vendors`,  // append `/:id/payout-summary`
        method : 'get'
    },

    // ── Marketplace offers (Stage 5) ────────────────────────────────────
    // Public: every seller's offer on a product, per variant, cheapest first.
    productOffers : {
        url : `${backendDomain}/api/v1/products`,  // append `/:id/offers`
        method : 'get'
    },
    // Pre-flight cart availability check (checkout blocks on failures).
    validateCart : {
        url : `${backendDomain}/api/v1/cart/validate`,
        method : 'post'
    },
    // Vendor listings — offers a vendor placed on other sellers' products.
    vendorListings : {
        url : `${backendDomain}/api/v1/vendor/listings`,
        method : 'get'
    },
    vendorCreateListing : {
        url : `${backendDomain}/api/v1/vendor/listings`,
        method : 'post'
    },
    vendorUpdateListing : {
        url : `${backendDomain}/api/v1/vendor/listings`,  // append `/:skuId`
        method : 'PATCH'
    },
}


export default SummaryApi
